/** @format */

import { BehaviorSubject, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { AthleteManagementDao } from '@my-account/data-access/dao';
import { AthleteManagementIdentityService } from '@my-account/data-access/services';
import { IdentityV2 } from '@my-account/data-access/interfaces';
import { Injectable } from '@angular/core';
import {
	AddressV2,
	AthleteManagementAddressResponse,
	PersonalInformation
} from '@shared/data-access/interfaces';
import { Store } from '@shared/data-access/services';

@Injectable({
	providedIn: 'root'
})
export class AthleteManagementAddressService {
	constructor(
		private dao: AthleteManagementDao,
		private store: Store,
		private athleteManagementIdentityService: AthleteManagementIdentityService
	) {}

	private addressChangedSubject = new BehaviorSubject<void>(null);
	public addressChanged$ = this.addressChangedSubject.asObservable();

	public createAddress(address: AddressV2): Observable<AthleteManagementAddressResponse> {
		return this.dao.createAddress(address).pipe(
			tap((result) => {
				this.store
					.selectDistinctKey<PersonalInformation>('PERSONAL_INFO')
					.subscribe((user: PersonalInformation) => {
						address.addressId = result.addressId;
						address.saved = true;
						user.addressesV2.unshift(address);
						this.store.set('PERSONAL_INFO', user);
					});
				return result;
			})
		);
	}

	public updateAddress(address: AddressV2): Observable<AthleteManagementAddressResponse> {
		return this.dao.updateAddress(address).pipe(
			tap((result) => {
				this.store
					.selectDistinctKey<PersonalInformation>('PERSONAL_INFO')
					.subscribe((user: PersonalInformation) => {
						const addressIndex = user.addressesV2.findIndex(
							(x) => x.addressId === address.addressId
						);
						if (addressIndex > -1) {
							address.addressId = result.addressId;
							user.addressesV2.splice(addressIndex, 1);
							user.addressesV2.unshift(address);
							this.store.set('PERSONAL_INFO', user);
						}
					});
				return result;
			})
		);
	}

	public deleteAddress(addressId: string): Observable<string> {
		return this.dao.deleteAddress(addressId).pipe(
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			tap((result) => {
				this.store
					.selectDistinctKey<PersonalInformation>('PERSONAL_INFO')
					.subscribe((user: PersonalInformation) => {
						const addressIndex = user.addressesV2.findIndex(
							(x) => x.addressId === addressId
						);
						user.addressesV2.splice(addressIndex, 1);
						this.store.set('PERSONAL_INFO', user);
					});
			})
		);
	}

	public getAddress(): Observable<AddressV2[]> {
		return this.athleteManagementIdentityService.getIdentity().pipe(
			tap((identityV2: IdentityV2) => {
				this.store
					.select<PersonalInformation>('PERSONAL_INFO')
					.subscribe((user: PersonalInformation) => {
						user.primaryAddress = identityV2.primaryAddress;
						this.store.set('PERSONAL_INFO', user);
					});
			}),
			switchMap(() => this.dao.getAddress())
		);
	}

	public notifyAddressChanged(): void {
		this.addressChangedSubject.next();
	}
}
